import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES, SLOT_FUND, SLOT_GROUP, WRITE_FLAGS } from "./_shared.js";

/**
 * `COL-RECURRING` (@UC0220) — mở **LỊCH thu định kỳ** (T2). Trang PO yêu cầu bổ sung 17/09 sau khi đọc Sổ: *"mở đợt thu theo tháng
 * 500k mỗi người"* không phải một đợt lẻ, và nhét nó vào `COL-EQUAL` thì người dùng nói "theo tháng" mà máy ghi một lần.
 * Executor = `CollectionScheduleService.createSchedule` (cùng cửa `POST /v1/funds/:id/collection-schedules`: tạo lịch + sinh KỲ ĐẦU).
 *
 * 🔴 **KHÔNG hỏi hạn — và đó là cách DUY NHẤT để có "hạn mỗi kỳ".** PO liệt kê một ô "hạn mỗi kỳ", nhưng đọc code thì ngược:
 * `collection-schedule.service.ts` đặt `perPeriodDueDate = (dueDate === null)` — **vắng `dueDate` ⇒ mỗi kỳ tự tính hạn của kỳ đó**,
 * còn khai `dueDate` tường minh ⇒ **một ngày CỐ ĐỊNH thắng cho mọi kỳ** (đường cũ của admin). Nghĩa là hỏi ô hạn chính là TẮT thứ PO
 * vừa yêu cầu. Trang vì thế không hỏi, binding không gửi `dueDate` — nhưng theo luật của 0034 (*trang không hỏi thì trang phải NÓI*),
 * phiếu **in hạn KỲ ĐẦU**, tính bằng CHÍNH `resolvePeriodDueDate` mà service dùng (thuần ⇒ preview ≡ execute).
 *
 * 🔴 **Chu kỳ THU HẸP có chủ đích, không phải map thiếu.** `collectionFrequencySchema` có 6 giá trị
 * (`weekly monthly quarterly yearly school_year semester`); Sổ mở **hai** (`hằng tháng`, `hằng quý`) theo QĐ PO. Bốn cái còn lại là
 * việc của màn hình (`COL-SCHEDULE-MANAGE`) cho tới khi PO mở thêm — nhãn↔code sống trong binding, int-spec so HAI CHIỀU với enum
 * của contracts nên thêm/bớt một chu kỳ là đỏ, không trôi.
 *
 * ✅ **`US-AIV2-0034d` ĐÃ XONG (27/09) — điều kiện lên `live` của trang này đã thoả.** QĐ điều phối 18/09 (sau review #837 M-1) cấm
 * `live` khi TOCTOU còn mở: reviewer đo hai phiếu Đồng ý **đồng thời** ⇒ `schedules=2 · collections=2 · member_dues=4`. Nay cửa
 * `createSchedule` có khoá nội dung (quỹ · số tiền · chu kỳ) + partial UNIQUE `collection_schedules_active_content_uniq` trên lịch
 * ĐANG CHẠY ⇒ kẻ thua nhận 409, 1 lịch. `col-recurring.page.test.ts` khoá CHÍNH điều kiện đó (đọc migration): gỡ ràng buộc ⇒ đỏ.
 *
 * 🔴 **Hai lịch y hệt = hai bộ nghĩa vụ MỖI KỲ, lặp lại mãi** — nặng hơn đợt thu lẻ (sai một lần). Cửa GUI tự REPLAY khi khớp khoá
 * nội dung; trang khai `executorMayNoop`, binding truyền `onReplay` để biết lượt đó không ghi gì, và câu `doneAlready` nói thật.
 * Đây là ca THỨ HAI của luật dựng ở 0034 (review #832 MED-1).
 */
export const COL_RECURRING = {
  id: "COL-RECURRING",
  uc: "@UC0220",
  tier: "T2",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    SLOT_GROUP,
    SLOT_FUND,
    { id: "soTien", type: "money", required: true, ask: "Mỗi người đóng bao nhiêu MỖI KỲ?" },
    {
      id: "chuKy",
      type: "enum",
      required: true,
      ask: "Thu theo chu kỳ nào? (hằng tháng · hằng quý)",
      choices: ["hằng tháng", "hằng quý"],
      /**
       * 🔴 US-AIV2-0112 H02 (@UC0220) — CÁCH NGƯỜI VIỆT NÓI chu kỳ + lưới đỡ suy từ câu. Đo 28/09: *"hàng tháng mỗi người đóng quỹ đội 100k"*
       * ⇒ model không gạch ô này (4/4 lượt) ⇒ hỏi lại điều vừa nói. "hang thang" (không dấu) khớp nhãn; cụm dưới là phần còn thiếu. Không có "tháng"/"quý" MỘT MÌNH: "từ tháng sau" là ngày bắt đầu, "3 tháng" là độ dài, "quý" trùng "quỹ" khi gõ không dấu.
       * Câu phủ định/nghi vấn ⇒ hỏi ở MỌI nhánh (`fromSentence`). Không mặc định: trang ghi không đoán hộ một tham số đẻ ra tiền mỗi kỳ.
       */
      synonyms: {
        // "hàng …" là cách viết khác DẤU của nhãn — lưới câu so theo dấu (review #1070 H1) nên phải khai.
        "hằng tháng": ["hàng tháng", "mỗi tháng", "tháng nào cũng", "theo tháng", "từng tháng", "một tháng một lần", "1 tháng 1 lần"],
        "hằng quý": ["hàng quý", "mỗi quý", "quý nào cũng", "theo quý", "từng quý", "ba tháng một lần", "3 tháng một lần", "3 tháng 1 lần"],
      },
      // review #1070 H2: "mỗi tháng 2 lần" không phải hằng tháng — phủ quyết đếm lần của nghĩa `sole-frequency`.
      fromSentence: "sole-frequency",
    },
    {
      id: "ngayBatDau",
      type: "date",
      required: true,
      ask: "Bắt đầu thu từ ngày nào?",
      // 🔴 UX-COLLECTION-PERIOD (@UC0220 · nghiệm thu UX-H3 15/10): "thu quy thang 11 moi nguoi 150k" (LLM thật) ⇒ trang này hỏi «Bắt đầu thu từ ngày nào?»
      // mà không một chữ nói đây là LỊCH thu lặp lại ⇒ người dùng không biết mình đang ở việc nào. Câu NGƯỜI DÙNG THẤY nói việc + chu kỳ đã chốt (ô
      // `chuKy` hỏi trước ô này); `ask` giữ nguyên chữ cũ vì nó vào prompt (đổi = mục promptHash).
      shownAsk: "Đây là LỊCH thu định kỳ {chuKy} — mỗi kỳ mọi người đóng một lần. Bắt đầu thu từ ngày nào?",
    },
  ],
  llm: {
    description: "Mở một LỊCH thu lặp lại (hằng tháng hoặc hằng quý), mỗi kỳ mỗi người đóng cùng một số tiền — không phải đợt thu một lần.",
    examples: [
      "mở đợt thu theo tháng 500k mỗi người",
      "mo dot thu theo thang 500k moi nguoi",
      "thu quỹ hằng tháng 200k",
      "thu quy hang thang 200k",
      "lập lịch thu mỗi tháng 300k một bạn",
      "lap lich thu moi thang 300k",
      "thu định kỳ hằng quý 1 triệu",
      "thu dinh ky hang quy 1 trieu",
      "mỗi quý thu 500k",
      "thu 200k/tháng",
      "tạo lịch thu định kỳ",
      "thu hằng tháng",
      // ——— 0105: thêm cho đủ 40 ———
      "đặt lịch thu quỹ mỗi tháng",
      "dat lich thu quy moi thang",
      "mỗi tháng thu 150k một người, lặp lại luôn",
      "moi thang thu 150k mot nguoi lap lai luon",
      "cho thu tự động hằng tháng",
      "cho thu tu dong hang thang",
      "lập lịch thu quý cho quỹ lớp",
      "lap lich thu quy cho quy lop",
      "thu góp hằng tháng 100 nghìn",
      "thu gop hang thang 100 nghin",
      "muốn mỗi tháng tự mở một đợt thu",
      "muon moi thang tu mo mot dot thu",
      "tạo lịch thu lặp lại 250k mỗi kỳ",
      "tao lich thu lap lai 250k moi ky",
      "quỹ đội bóng thu 300k mỗi quý",
      "quy doi bong thu 300k moi quy",
      "đặt thu định kỳ từ tháng sau",
      "dat thu dinh ky tu thang sau",
      "mở lịch thu hằng quý",
      "mo lich thu hang quy",
      "làm sao để thu quỹ tự lặp mỗi tháng?",
      "lam sao de thu quy tu lap moi thang",
      "thu đều hằng tháng mỗi người như nhau",
      "thu deu hang thang moi nguoi nhu nhau",
      "cài lịch thu 2 triệu mỗi quý",
      "cai lich thu 2 trieu moi quy",
      "đóng góp hằng tháng cho quỹ hội đồng hương",
      "dong gop hang thang cho quy hoi dong huong",
    ],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ eval đo được.
    questionsNotHere: ["nhóm đang có đợt thu nào không"],
  },
  /**
   * 🔴 US-AIV2-0110 S03 (@UC0260 · QĐ PO 4) — ĐÁP ÁN TIỀN của bộ eval, VIẾT TAY từ chính câu (không từ đầu ra model).
   * KHÔNG vào prompt ⇒ không đổi hash theo ca. Câu vắng mặt ở đây: không nói số tiền, hoặc mơ hồ bậc (§4).
   */
  evalAmounts: {
    "mở đợt thu theo tháng 500k mỗi người": "500000",
    "mo dot thu theo thang 500k moi nguoi": "500000",
    "thu quỹ hằng tháng 200k": "200000",
    "thu quy hang thang 200k": "200000",
    "lập lịch thu mỗi tháng 300k một bạn": "300000",
    "lap lich thu moi thang 300k": "300000",
    "thu định kỳ hằng quý 1 triệu": "1000000",
    "thu dinh ky hang quy 1 trieu": "1000000",
    "mỗi quý thu 500k": "500000",
    "thu 200k/tháng": "200000",
    "mỗi tháng thu 150k một người, lặp lại luôn": "150000",
    "moi thang thu 150k mot nguoi lap lai luon": "150000",
    "thu góp hằng tháng 100 nghìn": "100000",
    "thu gop hang thang 100 nghin": "100000",
    "tạo lịch thu lặp lại 250k mỗi kỳ": "250000",
    "tao lich thu lap lai 250k moi ky": "250000",
    "quỹ đội bóng thu 300k mỗi quý": "300000",
    "quy doi bong thu 300k moi quy": "300000",
    "cài lịch thu 2 triệu mỗi quý": "2000000",
    "cai lich thu 2 trieu moi quy": "2000000",
  },
  executor: "collectionSchedule.createSchedule",
  // Cửa `createSchedule` REPLAY khi quỹ đã có lịch ĐANG CHẠY cùng nội dung (US-AIV2-0034d) ⇒ lượt đó KHÔNG ghi gì (review #832 MED-1).
  executorMayNoop: true,
  /** AI-COL-OPEN-PHRASING (@UC0195 @UC0220): lưới mở đợt thu theo đầu người — kiểu đợt + ô nhận chữ của câu (`collection-open.ts`). Không vào prompt ⇒ 0 ca eval đổi hash. */
  collectionFromSentence: { cadence: "recurring", amount: "soTien" },
  /** `@UC0216` — cửa MỞ lịch chuyển từ `COL-SCHEDULE-MANAGE` sang đây, đúng bàn giao 0049 viết sẵn ở trang đó. */
  endpoints: ["POST /funds/{fundId}/collection-schedules"],
  facts: ["soNguoi", "tongMoiKy", "tenLichGhi", "chuKyGhi", "kyDau", "hanKyDau"],
  templates: {
    confirm:
      "Mở lịch thu “{tenLichGhi}” ở nhóm {nhom}, quỹ {quy}: {chuKyGhi}, mỗi người {soTien} một kỳ, {soNguoi} người, tổng {tongMoiKy} mỗi kỳ. Bắt đầu {ngayBatDau} — kỳ đầu {kyDau}, hạn {hanKyDau}?",
    done:
      "Đã mở lịch thu “{tenLichGhi}” — {chuKyGhi}, {soNguoi} người × {soTien} = {tongMoiKy} mỗi kỳ. Kỳ đầu {kyDau} đã mở, hạn {hanKyDau}; các kỳ sau hệ thống tự mở, mỗi kỳ một hạn riêng.",
    /**
     * Lượt KHÔNG ghi gì: ĐÃ có lịch đang chạy cùng quỹ + cùng số tiền + cùng chu kỳ. Nói thẳng là không tạo thêm, và nói VÌ SAO nó
     * đáng sợ hơn một đợt lẻ — hai lịch nghĩa là mỗi kỳ cả nhóm bị đòi hai lần. Cửa thoát: đổi số tiền/chu kỳ, hoặc sửa lịch đang có ở màn hình.
     *
     * 🔴🔴 CÂU NÀY CHỈ ĐƯỢC KHẲNG ĐỊNH BA THỨ KHOÁ SO KHỚP BẢO ĐẢM: **quỹ · số tiền · chu kỳ**. Không TÊN lịch, không kỳ đầu/hạn.
     *
     * Vì sao (đo được, đừng "sửa lại cho đầy đủ thông tin"): `AiDoerService` so TỪNG khoá facts phiếu ↔ facts `execute` rồi rollback
     * khi lệch, nên binding **buộc** phải trả lại facts của **KẾ HOẠCH** cho lượt không-ghi — nó không được phép thay bằng dữ liệu của
     * bản ghi đã có. Mà bản ghi khớp có thể là lịch tạo từ **màn hình GUI**, mang tên khác và ngày bắt đầu khác. Gọi tên kế hoạch ra
     * ⇒ câu chữ chỉ người dùng đi tìm một lịch KHÔNG TỒN TẠI. Khoá bằng ca `doneAlready` trong `col-recurring.page.test.ts` (đo trên
     * chỗ trống của template, không trên chuỗi đã render).
     */
    doneAlready:
      "Quỹ {quy} đã có một lịch thu {chuKyGhi} {soTien} mỗi người một kỳ đang chạy rồi, nên mình KHÔNG tạo thêm — hai lịch giống nhau nghĩa là mỗi kỳ cả nhóm bị thu hai lần. Muốn đổi số tiền hay chu kỳ thì sửa lịch đang có trong màn hình lịch thu.",
  },
  labels: {
    tenLichGhi: "Tên lịch thu",
    nhom: "Nhóm",
    quy: "Quỹ",
    soTien: "Mỗi người mỗi kỳ",
    chuKyGhi: "Chu kỳ",
    soNguoi: "Số người",
    tongMoiKy: "Tổng mỗi kỳ",
    // US-AIV2-0113 M02 (@UC0161): ngày bắt đầu ĐÃ HIỂU (ô — đóng băng trên phiếu, như `hanChot` của COL-DEADLINE). "từ tháng sau" ⇒ ngày 1 tháng kế
    // in ra đây để người dùng sửa được trước khi bấm Đồng ý; không in thì "từ 15/10" và "từ 01/10" ra cùng một phiếu ("Kỳ đầu: Tháng 10").
    ngayBatDau: "Bắt đầu",
    kyDau: "Kỳ đầu",
    hanKyDau: "Hạn kỳ đầu",
  },
  journey: { outcomes: { done: { next: ["READ-COLLECTIONS", "DEBT-REMIND"] }, done_already: { next: ["READ-COLLECTIONS", "DEBT-REMIND"] } } },
  help: { title: "Mở lịch thu định kỳ", tryNow: "thu quỹ hằng tháng 200k" },
} satisfies AiCapabilityPage;
